/**
 * Country of the profile (`users.countryCode`, docs/05; Geri, #96).
 *
 * ISO 3166-1 alpha-2, upper case („HU"); `null` = not given. Public: the flag
 * shows on the own and on the foreign profile. The client writes the field
 * (`firestore.rules` → users update list) — never filled from GPS.
 *
 * The country list is the set of flag files we ship (`flag-icons`, one SVG per
 * country, loaded one by one — see `CountryFlag`), minus the codes that are not
 * countries. Names come from `Intl.DisplayNames` in the UI language, not from a
 * package.
 */

import { doc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

/**
 * Lazy URL loaders for every 4:3 flag. Each SVG is its own asset and is only
 * fetched when a flag is actually shown — never the whole set.
 */
const FLAG_LOADERS = import.meta.glob<string>('/node_modules/flag-icons/flags/4x3/??.svg', {
  query: '?url',
  import: 'default',
});

/** Flag files that are not ISO 3166-1 countries (organisations, reserved codes). */
const NOT_COUNTRIES = new Set(['EU', 'UN', 'XX', 'PC', 'CP', 'DG', 'IC']);

const FLAG_BY_CODE = new Map<string, () => Promise<string>>();
for (const [path, load] of Object.entries(FLAG_LOADERS)) {
  const name = path.slice(path.lastIndexOf('/') + 1, -'.svg'.length);
  if (/^[a-z]{2}$/.test(name) && !NOT_COUNTRIES.has(name.toUpperCase())) {
    FLAG_BY_CODE.set(name.toUpperCase(), load);
  }
}

/** Every selectable country code, upper case, alphabetical by code. */
export const COUNTRY_CODES: readonly string[] = [...FLAG_BY_CODE.keys()].sort();

/** A stored or typed value as a valid country code, or `null`. */
export function normalizeCountryCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  return FLAG_BY_CODE.has(code) ? code : null;
}

/** The URL of the country's flag SVG; loads only that one file. */
export function loadFlagUrl(code: string): Promise<string> | null {
  const load = FLAG_BY_CODE.get(code.toUpperCase());
  return load ? load() : null;
}

/**
 * The device region as a country: `hu-HU` → `HU`. Only the FIRST preferred
 * language counts, and a tag without a region (`hu`) gives `null` — a later
 * `en-US` in the list says nothing about where the user lives (docs/02 →
 * Profilbeállító varázsló → Ország).
 */
export function countryFromLanguageTags(tags: readonly string[]): string | null {
  const first = tags[0];
  if (!first) return null;
  try {
    return normalizeCountryCode(new Intl.Locale(first).region);
  } catch {
    return null;
  }
}

/** Country name in the given UI language; the code itself as a last resort. */
export function countryName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region', fallback: 'code' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** All countries with their names, sorted by name in the UI language. */
export function countryOptions(locale: string): { code: string; name: string }[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale], { type: 'region', fallback: 'code' });
  } catch {
    names = null;
  }
  const collator = new Intl.Collator(locale);
  return COUNTRY_CODES.map((code) => ({ code, name: names?.of(code) ?? code })).sort((a, b) =>
    collator.compare(a.name, b.name),
  );
}

/** Saves the country (or clears it with `null`) on the own profile document. */
export async function saveCountryCode(uid: string, code: string | null): Promise<void> {
  if (!db) throw new Error('firestore_unavailable');
  await setDoc(
    doc(db, 'users', uid),
    { countryCode: code === null ? null : normalizeCountryCode(code), updatedAt: new Date() },
    { merge: true },
  );
}
